import { createRoute, Link } from "@tanstack/react-router";
import {
  ArrowLeft,
  Building2,
  CheckCircle2,
  Clock3,
  ExternalLink,
  Loader2,
  XCircle,
} from "lucide-react";
import { Route as patientLayout } from "./patient-layout";
import { PageShell } from "@/frontend/components/dashboard/PageShell";
import { cn } from "@/frontend/lib/utils";
import { formatDateTime, logoUrl } from "@/frontend/utils/shared";
import { isConnectionHealthy } from "@/frontend/utils/dashboard/connections";
import {
  CONNECTION_CHANNEL_LABEL,
  CONNECTION_CHANNEL_TONE,
  CONNECTION_STATUS_LABEL,
} from "@/frontend/types/dashboard/connections";
import {
  usePatientAuthEventsQuery,
  usePatientConnectionsQuery,
  usePatientRecordsStatsQuery,
} from "@/frontend/queries/dashboard/patients";
import { ERROR_TYPE_MESSAGES } from "@/frontend/constants/retrieval-errors";

export const Route = createRoute({
  getParentRoute: () => patientLayout,
  path: "/connections/$connectionId",
  component: ConnectionDetailPage,
});

/** One label/value pair in the detail grid. */
function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-[10px] font-medium uppercase tracking-wide text-foreground">
        {label}
      </dt>
      <dd className="mt-0.5 truncate text-xs font-medium" title={value}>
        {value}
      </dd>
    </div>
  );
}

/**
 * One connected health system, as its own page rather than a drawer. A
 * connection carries status, scopes, pull statistics, fetch errors and the full
 * sign-in history, which is more than a drawer can show and worth a linkable URL
 * when someone is chasing why a source stopped returning records.
 */
function ConnectionDetailPage() {
  const { patientId, connectionId } = Route.useParams();
  const { data: connectionList, isLoading } = usePatientConnectionsQuery(patientId);

  const connection = connectionList?.connections.find((row) => row.id === connectionId);
  // `fhir_auth_event.provider_id` holds the FHIR base URL, not the source id.
  // Filtering by `sourceId` matched nothing, so this panel was always empty.
  const { data: authEvents, isLoading: eventsLoading } = usePatientAuthEventsQuery(
    patientId,
    connection?.sourceFhirBaseUrl ?? "",
    !!connection?.sourceFhirBaseUrl,
  );
  const { data: recordsStats } = usePatientRecordsStatsQuery(patientId, !!connection);
  const accountStats = recordsStats?.perAccount.find(
    (account) => account.accountId === connection?.accountId,
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="size-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!connection) {
    return (
      <PageShell className="py-6">
        <p className="text-xs text-muted-foreground">
          This connection no longer exists.{" "}
          <Link
            to="/dashboard/patients/$patientId/onboarding"
            params={{ patientId }}
            className="text-primary hover:underline"
          >
            Back to onboarding
          </Link>
        </p>
      </PageShell>
    );
  }

  const url = logoUrl(connection.sourceLogo);
  const healthy = isConnectionHealthy(connection.status);

  return (
    <>
      <div className="border-b">
        <PageShell className="pb-5 pt-6">
          <Link
            to="/dashboard/patients/$patientId/onboarding"
            params={{ patientId }}
            className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="size-3.5" />
            All health systems
          </Link>

          <div className="mt-3 flex flex-wrap items-center gap-3">
            <span className="flex size-11 shrink-0 items-center justify-center overflow-hidden rounded-lg border bg-background">
              {url ? (
                <img
                  src={url}
                  alt=""
                  className="size-6 object-contain"
                  data-ph-no-capture
                  onError={(event) => {
                    event.currentTarget.style.display = "none";
                  }}
                />
              ) : (
                <Building2 className="size-5 text-muted-foreground" />
              )}
            </span>
            <div className="min-w-0">
              {/* Two lines, not an ellipsis: systems sharing a long prefix
                  ellipsize to the same string and stop being distinguishable. */}
              <h1 className="line-clamp-2 text-lg font-semibold tracking-tight" data-patient>
                {connection.sourceName}
              </h1>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <span
                  className={cn(
                    "rounded px-1.5 py-0.5 text-[10px] font-medium",
                    CONNECTION_CHANNEL_TONE[connection.channel],
                  )}
                >
                  {CONNECTION_CHANNEL_LABEL[connection.channel]}
                </span>
                <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <span
                    className={cn(
                      "size-1.5 rounded-full",
                      healthy ? "bg-emerald-500" : "bg-amber-500",
                    )}
                  />
                  {CONNECTION_STATUS_LABEL[connection.status] ?? connection.status}
                </span>
              </div>
            </div>

            {connection.sourcePortalUrl && (
              <a
                href={connection.sourcePortalUrl}
                target="_blank"
                rel="noreferrer"
                className="ml-auto flex items-center gap-1.5 text-xs text-primary hover:underline"
              >
                Patient portal
                <ExternalLink className="size-3" />
              </a>
            )}
          </div>
        </PageShell>
      </div>

      <PageShell className="flex flex-col gap-5 py-6">
        {!healthy && (
          <div className="flex items-start gap-2.5 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3">
            <XCircle className="mt-0.5 size-4 shrink-0 text-amber-600 dark:text-amber-400" />
            <div>
              <p className="text-xs font-medium text-amber-700 dark:text-amber-400">
                {CONNECTION_STATUS_LABEL[connection.status] ?? connection.status}
              </p>
              <p className="mt-0.5 text-xs text-amber-700/80 dark:text-amber-400/80">
                {(connection.failureCode && ERROR_TYPE_MESSAGES[connection.failureCode]) ??
                  "This source has stopped returning records. Send a new link so the patient can sign in again."}
              </p>
            </div>
          </div>
        )}

        <section className="rounded-lg border">
          <div className="border-b px-4 py-3">
            <h2 className="text-xs font-semibold">Connection</h2>
          </div>
          <dl className="grid gap-4 px-4 py-4 sm:grid-cols-3 lg:grid-cols-5">
            <Detail label="Records stored" value={connection.recordCount.toLocaleString()} />
            <Detail
              label="Last pull"
              value={connection.lastPullAt ? formatDateTime(connection.lastPullAt) : "Never"}
            />
            <Detail label="Connected" value={formatDateTime(connection.createdAt)} />
            <Detail
              label="Failed fetches"
              value={(accountStats?.errorCount ?? 0).toLocaleString()}
            />
            <Detail label="Connection id" value={connection.publicId} />
          </dl>
        </section>

        <div className="grid gap-5 lg:grid-cols-2">
          <section className="rounded-lg border">
            <div className="border-b px-4 py-3">
              <h2 className="text-xs font-semibold">Sign-in history</h2>
            </div>
            {eventsLoading ? (
              <div className="flex items-center gap-2 px-4 py-3">
                <Loader2 className="size-3 animate-spin text-muted-foreground" />
                <span className="text-xs text-muted-foreground">Loading…</span>
              </div>
            ) : !authEvents || authEvents.length === 0 ? (
              <p className="px-4 py-3 text-xs text-muted-foreground">
                No sign-in attempts recorded for this source.
              </p>
            ) : (
              <div className="divide-y">
                {authEvents.map((event) => (
                  <div key={event.id} className="flex items-start gap-2.5 px-4 py-2.5">
                    {event.status === "success" ? (
                      <CheckCircle2 className="mt-0.5 size-3.5 shrink-0 text-emerald-500" />
                    ) : event.status === "pending" ? (
                      <Clock3 className="mt-0.5 size-3.5 shrink-0 text-amber-500" />
                    ) : (
                      <XCircle className="mt-0.5 size-3.5 shrink-0 text-destructive" />
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium">
                        {event.status === "success"
                          ? "Signed in"
                          : event.status === "pending"
                            ? "Pending"
                            : "Failed"}
                        <span className="ml-1.5 font-normal text-muted-foreground">
                          {event.eventType === "link" ? "linking" : "sign-in"}
                        </span>
                      </p>
                      {event.errorDescription && (
                        <p className="mt-0.5 text-xs text-muted-foreground">
                          {event.errorDescription}
                        </p>
                      )}
                    </div>
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {formatDateTime(event.createdAt)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </section>

          <div className="flex flex-col gap-5">
            {accountStats && accountStats.uniqueErrors.length > 0 && (
              <section className="rounded-lg border">
                <div className="border-b px-4 py-3">
                  <h2 className="text-xs font-semibold">Fetch errors</h2>
                </div>
                <div className="divide-y">
                  {accountStats.uniqueErrors.map((error, index) => (
                    <div key={index} className="flex items-start gap-3 px-4 py-2.5">
                      <span className="min-w-0 flex-1 break-all font-mono text-[10px] text-destructive">
                        {error.message}
                      </span>
                      <span className="shrink-0 text-xs font-medium tabular-nums">
                        {error.count}
                      </span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {connection.scopes.length > 0 && (
              <section className="rounded-lg border">
                <div className="border-b px-4 py-3">
                  <h2 className="text-xs font-semibold">Granted scopes</h2>
                </div>
                <div className="flex flex-wrap gap-1.5 px-4 py-3">
                  {connection.scopes.map((scope) => (
                    <span
                      key={scope}
                      className="rounded border bg-muted/40 px-1.5 py-0.5 text-[10px] text-muted-foreground"
                    >
                      {scope}
                    </span>
                  ))}
                </div>
              </section>
            )}
          </div>
        </div>
      </PageShell>
    </>
  );
}
